import React, { useRef } from 'react';
import { InputProps, Switch, SwitchProps } from '@apia/theme';
import { useField } from '../hooks/useField';
import { useValidationClass } from '../hooks/useValidationClass';
import { FieldWrapper } from './FieldWrapper';
import { TDefaultFieldType } from './types';

export type TSwitchbox = Omit<
  SwitchProps,
  'onChange' | 'defaultChecked' | 'defaultValue'
> &
  Pick<InputProps, 'onChange'> &
  TDefaultFieldType<boolean>;

export const Switchbox = React.forwardRef<HTMLInputElement, TSwitchbox>(
  (
    {
      avoidSemicolon,
      className,
      label,
      onChange: outerOnChange,
      type,
      avoidRegisterIfExists,
      formName,
      initialValue,
      name,
      removeOnUnmount,
      submitValueParser,
      validationClass,
      validationFunction,
      validationRules,
      value,
      ...props
    },
    ref,
  ) => {
    const { actualValidationFunction, actualValidationRules } =
      useValidationClass({
        formName,
        name,
        validationClass,
        validationFunction,
        validationRules,
      });

    const {
      value: innerValue,
      onChange,
      updateValidations,
      isTouched,
      ...inputProps
    } = useField<boolean>({
      avoidRegisterIfExists,
      formName,
      name,
      initialValue,
      removeOnUnmount,
      submitValueParser,
      validationFunction: actualValidationFunction,
      validationRules: actualValidationRules,
    });

    const putValue = useRef(false);

    return (
      <FieldWrapper
        name={name}
        label={label}
        className={`checkbox ${className ?? ''}`}
        avoidSemicolon={avoidSemicolon}
        title={props.title}
      >
        <Switch
          {...props}
          {...inputProps}
          aria-label={props['aria-label'] ?? label}
          onChange={(ev) => {
            onChange(ev.target.checked);
            if (outerOnChange) {
              outerOnChange(ev);
            }
          }}
          value={innerValue ? 'on' : 'off'}
          ref={(el) => {
            (ref as any)?.(el);
            if (el && putValue.current !== innerValue) {
              putValue.current = innerValue;
              if (
                innerValue !== undefined &&
                innerValue !== null &&
                putValue.current !== (el as HTMLInputElement).checked
              ) {
                (el as HTMLInputElement).click();
              }
            }
          }}
        />
      </FieldWrapper>
    );
  },
);

Switchbox.displayName = 'Switchbox';
